import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Img from "@/components/Img";
import { BRANDS, APPLICATIONS, HERO_IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const SLIDE_MS = 7000;
const BRAND_LAYOUT = [
  { col: "lg:col-span-7", ratio: "aspect-[16/10]" },
  { col: "lg:col-span-5", ratio: "aspect-[4/5] lg:aspect-auto" },
  { col: "lg:col-span-12", ratio: "aspect-[16/10] lg:aspect-[21/9]" },
];

export default function Home() {
  const { t, lp } = useLanguage();
  const [slide, setSlide] = useState(0);
  const parallaxRef = useRef(null);
  const slides = t("home.slides");

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), SLIDE_MS);
    return () => clearInterval(id);
  }, [slide]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (parallaxRef.current) parallaxRef.current.style.transform = `translateY(${window.scrollY * 0.14}px)`;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div data-testid="home-page">
      <Seo title={t("seo.home.title")} description={t("seo.home.desc")} />

      {/* CINEMATIC HERO */}
      <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden grain" aria-label="Hero">
        <div className="absolute inset-0" aria-hidden="true">
          {HERO_IMAGES.map((id, i) => (
            <div key={id} className={`hero-slide absolute inset-0 ${i === slide ? "opacity-100" : "opacity-0"}`}>
              <Img id={id} priority={i === 0} className={`w-full h-full object-cover img-tone ${i === slide ? "anim-drift" : ""}`} />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-[#121316] via-[#121316]/50 to-[#121316]/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121316]/60 via-transparent to-transparent" />
        </div>

        <div ref={parallaxRef} className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-28 pt-40 will-change-transform">
          <div className="mono-tag text-[11px] sm:text-xs uppercase text-[#C2A675] tracking-[0.25em] mb-6 flex items-center gap-3">
            <span className="inline-block w-2 h-2 bg-[#C2A675]" aria-hidden="true" />
            {t("home.tag")}
          </div>
          <h1 className="serif-display text-5xl sm:text-6xl lg:text-8xl font-light leading-[1.02] tracking-tight mb-8 text-white">
            <span className="hero-line"><span>{t("home.h1a")}</span></span>
            <span className="hero-line"><span>{t("home.h1b")}</span></span>
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-end">
            <div className="lg:col-span-7">
              <p className="anim-sub text-base sm:text-lg text-[#E8E6E1]/90 font-light max-w-xl leading-relaxed mb-4">{t("home.intro")}</p>
              <p className="anim-sub text-base sm:text-lg text-[#E8E6E1]/90 font-light max-w-xl leading-relaxed mb-8">{t("home.intro2")}</p>
              <div className="anim-sub flex flex-wrap gap-4 items-center">
                <Link to={lp("/marcas")} data-testid="hero-cta-portfolio" className="btn-solid px-8 py-4">{t("home.cta1")}</Link>
                <Link to={lp("/contactos")} data-testid="hero-cta-manifesto" className="btn-ghost px-8 py-4">{t("home.cta2")}</Link>
              </div>
            </div>
            {/* Slide captions + indicators */}
            <div className="anim-sub lg:col-span-5 lg:pl-10" data-testid="hero-slide-nav">
              <div className="grid grid-cols-3 gap-4">
                {slides.map((s, i) => (
                  <button
                    key={s.a}
                    type="button"
                    onClick={() => setSlide(i)}
                    data-testid={`hero-slide-${i}`}
                    aria-label={s.a}
                    aria-current={i === slide ? "true" : undefined}
                    className="text-left group"
                  >
                    <div className={`slide-bar mb-3 ${i === slide ? "is-active" : ""}`}><span /></div>
                    <span className={`block mono-tag text-[10px] uppercase tracking-[0.2em] transition-colors ${i === slide ? "text-[#C2A675]" : "text-[#A8A6A0] group-hover:text-white"}`}>{s.a}</span>
                    <span className={`hidden sm:block text-[11px] font-light mt-1 leading-snug transition-colors ${i === slide ? "text-[#E8E6E1]" : "text-[#A8A6A0]/70"}`}>{s.b}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <a href="#confianca" data-testid="scroll-indicator" className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-[#A8A6A0] hover:text-[#C2A675] transition-colors" aria-label={t("home.scroll")}>
          <span className="relative w-px h-8 bg-white/15 overflow-hidden" aria-hidden="true"><span className="scroll-hint-dot absolute top-0 left-0 w-px h-3 bg-[#C2A675]" /></span>
          <ChevronDown size={14} aria-hidden="true" />
        </a>
      </section>

      {/* TRUST STRIP */}
      <div id="confianca" className="border-y border-white/10 bg-[#16181C]">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 text-sm text-[#D6D3CC] font-light">
          {t("home.trust").map((item, i) => (
            <Reveal key={i} delay={i * 0.1} className="flex items-start gap-3">
              <span className="mono-tag text-[10px] text-[#C2A675] mt-1 shrink-0">0{i + 1}</span>
              <span className="leading-snug">{item}</span>
            </Reveal>
          ))}
        </div>
      </div>

      {/* WHAT WE DO */}
      <section className="py-20 lg:py-24 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-6">
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("home.manifesto_tag")}</span>
            <h2 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6 leading-[1.05]">{t("home.manifesto_title")}</h2>
            {t("home.manifesto_text").map((p, i) => (
              <p key={i} className="text-base text-[#A8A6A0] leading-relaxed font-light max-w-lg mb-4 last:mb-0">{p}</p>
            ))}
            <Link to={lp("/a-igniva")} data-testid="manifesto-cta" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C2A675] hover:gap-3 transition-all">
              {t("home.manifesto_cta")} <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="lg:col-span-6 lg:pt-16">
            {t("home.roles").map((r, i) => (
              <Reveal key={r.t} delay={0.1 + i * 0.08} className="grid grid-cols-[2.5rem_1fr] gap-4 py-6 border-t border-white/10 last:border-b">
                <span className="mono-tag text-xs text-[#C2A675] pt-1">0{i + 1}</span>
                <div>
                  <h3 className="serif-display text-2xl font-light text-white mb-2">{r.t}</h3>
                  <p className="text-sm text-[#A8A6A0] font-light leading-relaxed">{r.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.1} className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-6" data-testid="home-sectors">
          <span className="mono-tag text-[10px] uppercase tracking-[0.22em] text-[#C2A675]">{t("home.sectors_tag")}</span>
          {t("home.sectors").map((s) => (
            <span key={s} className="text-xs uppercase tracking-[0.16em] text-[#D6D3CC]">{s}</span>
          ))}
        </Reveal>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-[#0E0F12] py-4 overflow-hidden" aria-hidden="true">
        <div className="mq-track items-center gap-12 text-xs uppercase tracking-[0.28em] text-[#A8A6A0]/70">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center gap-12 shrink-0">
              <span className="text-[#C2A675]">LITHOTECH</span><span>•</span>
              <span>FOSTER</span><span>•</span>
              <span className="text-[#C2A675]">MASTERCHARGE</span><span>•</span>
              <span>IGNIVA</span><span>•</span>
            </div>
          ))}
        </div>
      </div>

      {/* BRAND MOSAIC */}
      <section className="py-20 lg:py-24 max-w-7xl mx-auto px-6">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("home.brands_tag")}</span>
            <h2 className="serif-display text-4xl sm:text-5xl font-light text-white mt-3">{t("home.brands_title")}</h2>
          </div>
          <p className="text-sm text-[#A8A6A0] max-w-md font-light leading-relaxed">{t("home.brands_sub")}</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {BRANDS.map((b, i) => (
            <Reveal key={b.slug} delay={(i % 2) * 0.1} className={`${BRAND_LAYOUT[i].col} flex`}>
              <Link to={lp(`/marcas/${b.slug}`)} data-testid={`home-brand-${b.slug}`} className="group flex flex-col w-full">
                <div className={`img-frame border border-white/10 ${BRAND_LAYOUT[i].ratio} lg:flex-1 lg:min-h-[320px]`}>
                  <Img id={b.image} alt={b.name} label={t("common.pending_image")} className="absolute inset-0 w-full h-full object-cover img-tone" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C0D0F]/85 via-[#0C0D0F]/10 to-transparent" />
                  <div className="absolute top-5 left-5 right-5 flex flex-wrap gap-2">
                    <span className="mono-tag text-[10px] text-[#E8E6E1] uppercase tracking-[0.2em] px-2.5 py-1 bg-[#121316]/70 backdrop-blur-sm border border-white/10">{t(`brand.${b.slug}.tag`)}</span>
                    {b.exclusive && <span className="mono-tag text-[10px] text-[#121316] uppercase tracking-[0.2em] px-2.5 py-1 bg-[#C2A675]">{t("common.exclusive_pt")}</span>}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 flex items-end justify-between gap-6">
                    <div>
                      <h3 className="serif-display text-3xl sm:text-4xl font-light text-white">{b.name}</h3>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#C2A675] mt-1">{t(`brand.${b.slug}.origin`)}</p>
                    </div>
                    <span className="hidden sm:inline-flex items-center justify-center w-11 h-11 border border-white/25 text-white group-hover:bg-[#C2A675] group-hover:border-[#C2A675] group-hover:text-[#121316] transition-colors">
                      <ArrowRight size={16} aria-hidden="true" />
                    </span>
                  </div>
                </div>
                {t(`brand.${b.slug}.desc`).map((p, k) => (
                  <p key={k} className="text-sm text-[#A8A6A0] leading-relaxed font-light mt-3 first:mt-4 max-w-lg">{p}</p>
                ))}
                <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C2A675] group-hover:gap-3 transition-all">
                  {t("common.know_brand").replace("{name}", b.name)} <ArrowRight size={14} aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="py-20 lg:py-24 border-t border-white/10 bg-[#0E0F12]">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
            <div>
              <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("home.apps_tag")}</span>
              <h2 className="serif-display text-4xl sm:text-5xl font-light text-white mt-3">{t("home.apps_title")}</h2>
              <p className="text-sm text-[#A8A6A0] font-light mt-4 max-w-md leading-relaxed">{t("home.apps_intro")}</p>
            </div>
            <Link to={lp("/aplicacoes")} data-testid="home-apps-cta" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#C2A675] hover:gap-3 transition-all">
              {t("home.apps_cta")} <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {APPLICATIONS.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.08}>
                <Link to={lp("/aplicacoes")} className="group block img-frame aspect-[4/5] sm:aspect-[4/3] border border-white/10" data-testid={`home-app-${a.id}`}>
                  <Img id={a.image} alt={t(`app_items.${a.id}`)} label={t("common.pending_image")} className="absolute inset-0 w-full h-full object-cover img-tone" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent p-4 sm:p-6 flex flex-col justify-end">
                    <h3 className="serif-display text-white text-lg sm:text-2xl font-light leading-tight">{t(`app_items.${a.id}`)}</h3>
                    {a.brands && <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-[#C2A675] mt-1.5">{a.brands}</p>}
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPLORER TEASER */}
      <section className="py-20 lg:py-24 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <div className="img-frame anim-wipe aspect-[4/5] border border-white/15 shadow-2xl">
              <Img id={HERO_IMAGES[1]} alt={t("home.explorer_title")} className="absolute inset-0 w-full h-full object-cover img-tone" />
              <div className="absolute bottom-6 left-6 right-6 p-4 bg-[#121316]/85 backdrop-blur-md border border-white/10">
                <p className="mono-tag text-[10px] uppercase tracking-widest text-[#C2A675]">{slides[1].a}</p>
                <p className="text-xs text-[#E8E6E1] mt-1 font-light">{slides[1].b}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("home.explorer_tag")}</span>
            <h2 className="serif-display text-4xl sm:text-5xl font-light text-white mt-3 mb-6">{t("home.explorer_title")}</h2>
            <p className="text-base text-[#A8A6A0] leading-relaxed font-light max-w-lg mb-8">{t("home.explorer_text")}</p>
            <Link to={lp("/explorador")} data-testid="home-explorer-cta" className="btn-ghost px-8 py-4 border-[#C2A675]/60 text-[#C2A675] hover:!bg-[#C2A675] hover:!text-[#121316]">
              {t("home.explorer_cta")}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="border-t border-white/10 bg-[#0C0D0F]">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-8">
            <h2 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">{t("home.cta_title")}</h2>
            {t("home.cta_text").map((p, i) => (
              <p key={i} className={`text-base text-[#A8A6A0] font-light max-w-xl leading-relaxed ${i === 0 ? "mt-5" : "mt-3"}`}>{p}</p>
            ))}
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 flex flex-wrap gap-4 lg:justify-end">
            <Link to={lp("/orcamento")} data-testid="home-quote-cta" className="btn-solid px-10 py-5">{t("common.request_quote")}</Link>
            <Link to={lp("/contactos")} data-testid="home-contact-cta" className="btn-ghost px-10 py-5">{t("nav.contacts")}</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
